/* ==========================================================================
   Comfora – Produktseiten-JavaScript
   <media-gallery>, <bundle-picker>, <sticky-atc>, <size-finder>,
   <delivery-estimate>, <review-section>, <product-info>.

   Datenquelle: <script type="application/json" data-product-json> im Format
   von Shopify `{{ product | json }}` (variants, options, price in Cent).
   In Shopify einfach durch das Liquid-Objekt ersetzen.

   Wichtig: <product-info> wird zuletzt registriert, damit alle Kind-Elemente
   bereits ihre Event-Listener haben, wenn das erste Variant-Event feuert.
   ========================================================================== */
(function () {
  'use strict';

  const Comfora = window.Comfora;
  if (!Comfora) {
    console.error('[Comfora] theme.js muss vor product.js geladen werden.');
    return;
  }
  const { formatMoney } = Comfora;
  const displayColour = (value = '') => value.replaceAll('Weiß', 'White').replaceAll('Schwarz', 'Black').replaceAll('Steingrau', 'Stone Grey').replaceAll('Karamell', 'Caramel');

  const findVariant = (product, options) =>
    product.variants.find((variant) => options.every((value, index) => variant[`option${index + 1}`] === value)) || null;

  const uniqueOptionValues = (product, position) => [...new Set(product.variants.map((variant) => variant[`option${position}`]))];

  /* ------------------------------------------------------------------------
     Galerie: Scroll-Snap-Slider + Thumbnails, springt bei Farbwechsel
     ------------------------------------------------------------------------ */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-slides]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.thumbList = this.querySelector('[data-gallery-thumbs]');
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.prevButton = this.querySelector('[data-gallery-prev]');
      this.nextButton = this.querySelector('[data-gallery-next]');
      this.counter = this.querySelector('[data-gallery-current]');
      this.index = 0;

      this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.goTo(index)));
      this.prevButton?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.index + 1));

      this.track.addEventListener(
        'scroll',
        () => {
          window.cancelAnimationFrame(this.frame);
          this.frame = window.requestAnimationFrame(() => this.syncFromScroll());
        },
        { passive: true }
      );

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(this.index + 1);
        else if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
        else return;
        event.preventDefault();
      });

      this.closest('product-info')?.addEventListener('product:variant-change', (event) => {
        this.showColor(event.detail.options[0]);
      });

      this.setupLightbox();
      this.update();

      // Fester Startslide (data-start-slide, 1-basiert): beim Laden dorthin, nicht zum Farbbild springen.
      const startSlide = parseInt(this.dataset.startSlide, 10);
      if (startSlide > 0 && this.slides.length) {
        this.fixedStart = true;
        window.requestAnimationFrame(() => this.goTo(startSlide - 1, 'auto'));
      }
    }

    /* --- Vollbild-Ansicht mit Zoom (Tippen/Klicken, Pinch, Mausrad, Ziehen, Wischen) --- */
    setupLightbox() {
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      const zoomButton = this.querySelector('[data-gallery-zoom]');
      this.zoomImages = this.slides
        .map((slide, slideIndex) => ({ slideIndex, img: slide.querySelector('img[data-zoom-src]') }))
        .filter((entry) => entry.img);
      if (!this.lightbox || !this.zoomImages.length) {
        zoomButton?.remove();
        return;
      }

      this.lbImg = this.lightbox.querySelector('[data-lightbox-img]');
      this.lbStage = this.lightbox.querySelector('[data-lightbox-stage]');
      this.lbCurrent = this.lightbox.querySelector('[data-lightbox-current]');
      this.lbPrev = this.lightbox.querySelector('[data-lightbox-prev]');
      this.lbNext = this.lightbox.querySelector('[data-lightbox-next]');
      this.lightbox.querySelector('[data-lightbox-total]').textContent = String(this.zoomImages.length);
      this.lbIndex = 0;
      this.zoom = { scale: 1, x: 0, y: 0 };

      this.zoomImages.forEach(({ img }, position) => {
        img.classList.add('is-zoomable');
        img.addEventListener('click', () => this.openLightbox(position, this.track));
      });
      zoomButton?.addEventListener('click', () => {
        const position = this.zoomImages.findIndex((entry) => entry.slideIndex >= this.index);
        this.openLightbox(position < 0 ? 0 : position, zoomButton);
      });
      this.lbPrev.addEventListener('click', () => this.showLightbox(this.lbIndex - 1));
      this.lbNext.addEventListener('click', () => this.showLightbox(this.lbIndex + 1));
      this.lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.showLightbox(this.lbIndex + 1);
        else if (event.key === 'ArrowLeft') this.showLightbox(this.lbIndex - 1);
        else return;
        event.preventDefault();
      });
      this.lightbox.addEventListener('overlay:close', () => {
        this.setZoom(1, 0, 0);
        const slideIndex = this.zoomImages[this.lbIndex]?.slideIndex;
        if (slideIndex !== undefined && slideIndex !== this.index) this.goTo(slideIndex, 'auto');
      });
      this.setupZoomGestures();
    }

    openLightbox(position, opener) {
      this.showLightbox(position);
      Comfora.openOverlay(this.lightbox.id, opener);
    }

    showLightbox(position) {
      const total = this.zoomImages.length;
      this.lbIndex = Math.max(0, Math.min(total - 1, position));
      const { img } = this.zoomImages[this.lbIndex];
      this.setZoom(1, 0, 0);
      this.lbImg.src = img.dataset.zoomSrc;
      this.lbImg.alt = img.alt;
      this.lbCurrent.textContent = String(this.lbIndex + 1);
      this.lbPrev.disabled = this.lbIndex === 0;
      this.lbNext.disabled = this.lbIndex === total - 1;
      [this.lbIndex - 1, this.lbIndex + 1].forEach((index) => {
        const entry = this.zoomImages[index];
        if (entry) new Image().src = entry.img.dataset.zoomSrc;
      });
    }

    setZoom(scale, x, y) {
      const maxX = Math.max(0, (this.lbImg.offsetWidth * scale - this.lbStage.clientWidth) / 2);
      const maxY = Math.max(0, (this.lbImg.offsetHeight * scale - this.lbStage.clientHeight) / 2);
      const clamp = (value, limit) => Math.max(-limit, Math.min(limit, value));
      this.zoom = scale <= 1 ? { scale: 1, x: 0, y: 0 } : { scale, x: clamp(x, maxX), y: clamp(y, maxY) };
      this.lbImg.style.transform = `translate(${this.zoom.x}px, ${this.zoom.y}px) scale(${this.zoom.scale})`;
      this.lbStage.classList.toggle('is-zoomed', this.zoom.scale > 1);
    }

    setupZoomGestures() {
      const stage = this.lbStage;
      const pointers = new Map();
      const MAX_SCALE = 4;
      let start = null;
      let gesture = null;

      const toStage = (event) => {
        const rect = stage.getBoundingClientRect();
        return { x: event.clientX - rect.left - rect.width / 2, y: event.clientY - rect.top - rect.height / 2 };
      };
      const snapshot = () => {
        const points = [...pointers.values()];
        const base = { scale: this.zoom.scale, x: this.zoom.x, y: this.zoom.y, point: points[0] };
        if (points.length >= 2) {
          base.distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) || 1;
          base.mid = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
        }
        return base;
      };
      // Punkt p (relativ zur Bühnenmitte) bleibt beim Skalieren unter Finger/Maus
      const zoomAround = (point, fromZoom, scale) => {
        const contentX = (point.x - fromZoom.x) / fromZoom.scale;
        const contentY = (point.y - fromZoom.y) / fromZoom.scale;
        this.setZoom(scale, point.x - contentX * scale, point.y - contentY * scale);
      };

      stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        try {
          stage.setPointerCapture(event.pointerId);
        } catch (error) {
          // Zeiger ohne aktive Capture (z. B. sehr kurze Berührung) – Geste funktioniert trotzdem
        }
        if (!pointers.size) gesture = { moved: false, multi: false, swipe: 0 };
        pointers.set(event.pointerId, toStage(event));
        if (pointers.size > 1) gesture.multi = true;
        this.lbImg.classList.add('is-dragging');
        start = snapshot();
      });

      stage.addEventListener('pointermove', (event) => {
        if (!pointers.has(event.pointerId) || !start) return;
        pointers.set(event.pointerId, toStage(event));
        const points = [...pointers.values()];
        if (points.length >= 2 && start.distance) {
          const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
          const scale = Math.max(1, Math.min(MAX_SCALE, (start.scale * distance) / start.distance));
          const mid = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
          const contentX = (start.mid.x - start.x) / start.scale;
          const contentY = (start.mid.y - start.y) / start.scale;
          this.setZoom(scale, mid.x - contentX * scale, mid.y - contentY * scale);
          gesture.moved = true;
          return;
        }
        const dx = points[0].x - start.point.x;
        const dy = points[0].y - start.point.y;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) gesture.moved = true;
        if (this.zoom.scale > 1) this.setZoom(this.zoom.scale, start.x + dx, start.y + dy);
        else gesture.swipe = dx;
      });

      const end = (event) => {
        if (!pointers.has(event.pointerId)) return;
        pointers.delete(event.pointerId);
        if (pointers.size) {
          start = snapshot();
          return;
        }
        this.lbImg.classList.remove('is-dragging');
        if (event.type === 'pointercancel' || gesture.multi) return;
        if (!gesture.moved) {
          if (this.zoom.scale > 1) this.setZoom(1, 0, 0);
          else zoomAround(toStage(event), this.zoom, 2.5);
        } else if (this.zoom.scale === 1 && Math.abs(gesture.swipe) > 60) {
          this.showLightbox(this.lbIndex + (gesture.swipe < 0 ? 1 : -1));
        }
      };
      stage.addEventListener('pointerup', end);
      stage.addEventListener('pointercancel', end);

      stage.addEventListener(
        'wheel',
        (event) => {
          event.preventDefault();
          const scale = Math.max(1, Math.min(MAX_SCALE, this.zoom.scale * (event.deltaY < 0 ? 1.15 : 1 / 1.15)));
          zoomAround(toStage(event), this.zoom, scale);
        },
        { passive: false }
      );
    }

    goTo(index, behavior = Comfora.scrollBehavior()) {
      const next = Math.max(0, Math.min(this.slides.length - 1, index));
      this.track.scrollTo({ left: next * this.track.clientWidth, behavior });
      this.index = next;
      this.update();
    }

    syncFromScroll() {
      const width = this.track.clientWidth || 1;
      const index = Math.round(this.track.scrollLeft / width);
      if (index === this.index) return;
      this.index = index;
      this.update();
    }

    showColor(color) {
      if (!color || color === this.activeColor) return;
      const isInitial = this.activeColor === undefined;
      this.activeColor = color;
      if (isInitial && this.fixedStart) return;
      const index = this.slides.findIndex((slide) => slide.dataset.color === color);
      if (index >= 0 && index !== this.index) this.goTo(index, isInitial ? 'auto' : Comfora.scrollBehavior());
    }

    update() {
      this.thumbs.forEach((thumb, index) => thumb.setAttribute('aria-current', String(index === this.index)));
      if (this.prevButton) this.prevButton.disabled = this.index === 0;
      if (this.nextButton) this.nextButton.disabled = this.index === this.slides.length - 1;
      if (this.counter) this.counter.textContent = String(this.index + 1);

      const activeThumb = this.thumbs[this.index];
      if (activeThumb && this.thumbList && this.thumbList.scrollWidth > this.thumbList.clientWidth) {
        const target = activeThumb.parentElement.offsetLeft - this.thumbList.clientWidth / 2 + activeThumb.offsetWidth / 2;
        this.thumbList.scrollTo({ left: target, behavior: Comfora.scrollBehavior() });
      }
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ------------------------------------------------------------------------
     Bundle-Angebot (1 / 2 / 3 Paare) inkl. Auswahl für weitere Paare
     ------------------------------------------------------------------------ */
  class BundlePicker extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.product = Comfora.readJSON(this.productInfo?.querySelector('[data-product-json]'));
      if (!this.product) return;

      this.pairsContainer = this.querySelector('[data-bundle-pairs]');
      this.pairsList = this.querySelector('[data-bundle-pairs-list]');
      this.template = this.querySelector('template[data-pair-template]');
      this.colors = uniqueOptionValues(this.product, 1);
      this.sizes = uniqueOptionValues(this.product, 2);
      this.mainOptions = [];

      this.addEventListener('change', (event) => {
        const target = event.target;
        if (target.name === 'bundle') {
          this.renderPairs();
          this.dispatchEvent(new CustomEvent('bundle:change', { bubbles: true }));
          return;
        }
        const row = target.closest('.pair-row');
        if (!row) return;
        row.dataset.touched = 'true';
        target.classList.remove('is-invalid');
        if (target.matches('[data-pair-color]')) this.refreshRowSizes(row);
      });

      this.productInfo.addEventListener('product:variant-change', (event) => {
        this.mainOptions = event.detail.options;
        this.displayVariant = event.detail.displayVariant;
        this.renderPrices();
        this.syncUntouchedRows();
      });

      this.renderPrices();
      this.renderPairs();
    }

    get inputs() {
      return Array.from(this.querySelectorAll('input[name="bundle"]'));
    }

    get selectedInput() {
      return this.inputs.find((input) => input.checked) || this.inputs[0] || null;
    }

    get quantity() {
      return parseInt(this.selectedInput?.dataset.quantity, 10) || 1;
    }

    /**
     * Preis pro Paar für eine Staffel-Option (in Cent).
     * Bevorzugt einen festen Staffelpreis (data-unit-price), sonst Prozent-Rabatt (data-discount).
     */
    unitPriceFor(input, basePrice) {
      const fixed = parseInt(input?.dataset.unitPrice, 10);
      if (Number.isFinite(fixed) && fixed > 0) return Math.min(fixed, basePrice);
      const discount = parseFloat(input?.dataset.discount) || 0;
      return Math.round((basePrice * (100 - discount)) / 100);
    }

    unitPrice(basePrice) {
      return this.unitPriceFor(this.selectedInput, basePrice);
    }

    renderPrices() {
      const variant = this.displayVariant || this.product.variants[0];
      const compareUnit = Math.max(variant.compare_at_price || 0, variant.price);

      this.inputs.forEach((input) => {
        const option = input.closest('.bundle-option');
        if (!option) return;
        const quantity = parseInt(input.dataset.quantity, 10) || 1;
        const unit = this.unitPriceFor(input, variant.price);
        const savings = compareUnit > unit ? Math.round((1 - unit / compareUnit) * 100) : 0;

        const set = (selector, text) => {
          const el = option.querySelector(selector);
          if (el) el.textContent = text;
          return el;
        };
        set('[data-bundle-unit]', formatMoney(unit));
        set('[data-bundle-total]', quantity > 1 ? `${formatMoney(unit * quantity)} total` : 'Single pair');
        const compareEl = set('[data-bundle-compare]', savings ? formatMoney(compareUnit) : '');
        const saveEl = set('[data-bundle-save]', savings ? `Save ${savings}%` : '');
        if (compareEl) compareEl.hidden = !savings;
        if (saveEl) saveEl.hidden = !savings;
      });
    }

    renderPairs() {
      if (!this.pairsList || !this.template) return;
      const extraPairs = Math.max(0, this.quantity - 1);
      const rows = Array.from(this.pairsList.children);
      for (let i = rows.length; i < extraPairs; i += 1) this.pairsList.append(this.createRow(i + 2));
      rows.slice(extraPairs).forEach((row) => row.remove());
      if (this.pairsContainer) this.pairsContainer.hidden = extraPairs === 0;
    }

    createRow(pairNumber) {
      const row = this.template.content.firstElementChild.cloneNode(true);
      const colorSelect = row.querySelector('[data-pair-color]');
      const sizeSelect = row.querySelector('[data-pair-size]');
      row.querySelector('[data-pair-index]').textContent = String(pairNumber);
      colorSelect.setAttribute('aria-label', `Colour for pair ${pairNumber}`);
      sizeSelect.setAttribute('aria-label', `Size for pair ${pairNumber}`);

      colorSelect.replaceChildren(...this.colors.map((color) => new Option(displayColour(color), color)));

      this.applyMainSelection(row);
      return row;
    }

    applyMainSelection(row) {
      const [mainColor, mainSize] = this.mainOptions;
      row.querySelector('[data-pair-color]').value = mainColor || this.colors[0];
      this.refreshRowSizes(row, mainSize || '');
    }

    /**
     * Listet nur Größen, die es in der gewählten Farbe als Variante gibt.
     * Ausverkaufte Varianten bleiben sichtbar, sind aber gesperrt.
     */
    refreshRowSizes(row, preferredSize) {
      const color = row.querySelector('[data-pair-color]').value;
      const sizeSelect = row.querySelector('[data-pair-size]');
      const wanted = preferredSize ?? sizeSelect.value;

      const options = this.sizes.flatMap((size) => {
        const variant = findVariant(this.product, [color, size]);
        if (!variant) return [];
        const option = new Option(variant.available ? size : `${size} – sold out`, size);
        option.disabled = !variant.available;
        return [option];
      });

      sizeSelect.replaceChildren(new Option('Size', ''), ...options);
      sizeSelect.value = options.some((option) => option.value === wanted && !option.disabled) ? wanted : '';
    }

    syncUntouchedRows() {
      Array.from(this.pairsList?.children || []).forEach((row) => {
        if (row.dataset.touched !== 'true') this.applyMainSelection(row);
      });
    }

    /** Liefert Warenkorb-Positionen oder eine Fehlermeldung. */
    getLines(mainVariant) {
      const variants = [mainVariant];
      const rows = Array.from(this.pairsList?.children || []);

      for (const row of rows) {
        const pairNumber = row.querySelector('[data-pair-index]').textContent;
        const color = row.querySelector('[data-pair-color]').value;
        const sizeSelect = row.querySelector('[data-pair-size]');
        const variant = sizeSelect.value ? findVariant(this.product, [color, sizeSelect.value]) : null;
        if (!variant || !variant.available) {
          sizeSelect.classList.add('is-invalid');
          sizeSelect.focus();
          return { error: `Please choose an available size for pair ${pairNumber}.` };
        }
        variants.push(variant);
      }

      const input = this.selectedInput;
      const label = this.quantity > 1 ? input?.dataset.label || '' : '';
      const merged = new Map();
      variants.forEach((variant) => {
        const entry = merged.get(variant.id) || { variant, quantity: 0, unitPrice: this.unitPriceFor(input, variant.price), label };
        entry.quantity += 1;
        merged.set(variant.id, entry);
      });
      return { lines: Array.from(merged.values()) };
    }
  }
  customElements.define('bundle-picker', BundlePicker);

  /* ------------------------------------------------------------------------
     Sticky Add-to-Cart (erscheint, sobald der Haupt-Button aus dem Bild ist)
     ------------------------------------------------------------------------ */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.productInfo = document.querySelector(`product-info[data-handle="${this.dataset.handle}"]`);
      this.mainButton = this.productInfo?.querySelector('[data-main-atc]');
      if (!this.productInfo || !this.mainButton) return;

      this.image = this.querySelector('[data-sticky-image]');
      this.variantLabel = this.querySelector('[data-sticky-variant]');
      this.price = this.querySelector('[data-sticky-price]');
      this.compare = this.querySelector('[data-sticky-compare]');

      this.querySelector('[data-sticky-submit]')?.addEventListener('click', () => this.mainButton.click());
      this.productInfo.addEventListener('product:variant-change', (event) => this.render(event.detail));

      this.observer = new IntersectionObserver(([entry]) => {
        this.toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.observer.observe(this.mainButton);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    toggle(visible) {
      this.classList.toggle('is-visible', visible);
      this.inert = !visible;
      this.setAttribute('aria-hidden', String(!visible));
      document.body.classList.toggle('has-sticky-atc', visible);
    }

    render({ product, options, displayVariant }) {
      const [color, size] = options;
      const media = product.media.find((item) => item.color === color) || product.media[0];
      if (this.image && media) this.image.src = Comfora.assetUrl(media.src);
      if (this.variantLabel) this.variantLabel.textContent = `${displayColour(color)} · ${size ? `Size ${size}` : 'Choose a size'}`;
      if (this.price) this.price.textContent = formatMoney(displayVariant.price);
      if (this.compare) {
        const onSale = displayVariant.compare_at_price > displayVariant.price;
        this.compare.textContent = onSale ? formatMoney(displayVariant.compare_at_price) : '';
        this.compare.hidden = !onSale;
      }
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ------------------------------------------------------------------------
     Größenberater (Fußlänge → Größe)
     ------------------------------------------------------------------------ */
  class SizeFinder extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-foot-length]');
      this.result = this.querySelector('[data-size-result]');
      this.valueEl = this.querySelector('[data-size-value]');
      this.applyButton = this.querySelector('[data-apply-size]');
      this.message = this.querySelector('[data-size-message]');
      this.rows = Array.from(this.closest('dialog')?.querySelectorAll('[data-size-table] tr[data-size]') || []);
      if (!this.input || !this.rows.length) return;

      this.querySelector('[data-find-size]')?.addEventListener('click', () => this.calculate());
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.calculate();
      });
      this.applyButton?.addEventListener('click', () => this.apply());
    }

    showMessage(text) {
      this.message.textContent = text;
      this.message.hidden = false;
      this.result.hidden = true;
      this.input.setAttribute('aria-invalid', 'true');
    }

    calculate() {
      const length = parseFloat(this.input.value.replace(',', '.').trim());
      this.rows.forEach((row) => row.classList.remove('is-recommended'));
      this.recommended = null;

      if (!Number.isFinite(length) || length < 20 || length > 33) {
        this.showMessage('Please enter a foot length between 20 and 33 cm, e.g. 24.5.');
        return;
      }

      const row = this.rows.find((item) => length <= parseFloat(item.dataset.maxFoot));
      if (!row) {
        this.showMessage('Your foot length is above our largest size. Please contact us for advice.');
        return;
      }

      this.recommended = row.dataset.size;
      row.classList.add('is-recommended');
      this.valueEl.textContent = this.recommended;
      this.applyButton.textContent = `Select size ${this.recommended}`;
      this.input.removeAttribute('aria-invalid');
      this.message.hidden = true;
      this.result.hidden = false;
    }

    apply() {
      const productInfo = document.querySelector(`product-info[data-handle="${this.dataset.handle}"]`);
      if (!productInfo || !this.recommended) return;
      const selected = productInfo.selectOption(productInfo.sizeOptionName, this.recommended);
      const dialog = this.closest('dialog');
      if (dialog) Comfora.closeOverlay(dialog.id);
      Comfora.toast(
        selected
          ? `Size ${this.recommended} selected.`
          : `Size ${this.recommended} is not available in this colour. Please try another colour.`
      );
    }
  }
  customElements.define('size-finder', SizeFinder);

  /* ------------------------------------------------------------------------
     Lieferzeit-Schätzung (Werktage, Versand bis 14 Uhr am selben Tag)
     ------------------------------------------------------------------------ */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const output = this.querySelector('[data-delivery-range]');
      if (!output) return;

      const cutoffHour = parseInt(this.dataset.cutoff, 10) || 14;
      const minDays = parseInt(this.dataset.minDays, 10) || 2;
      const maxDays = parseInt(this.dataset.maxDays, 10) || 4;
      const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;
      const addBusinessDays = (date, days) => {
        const result = new Date(date);
        let added = 0;
        while (added < days) {
          result.setDate(result.getDate() + 1);
          if (!isWeekend(result)) added += 1;
        }
        return result;
      };

      const now = new Date();
      let shipDate = new Date(now);
      if (isWeekend(now) || now.getHours() >= cutoffHour) shipDate = addBusinessDays(now, 1);

      const format = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
      output.textContent = `${format.format(addBusinessDays(shipDate, minDays))} – ${format.format(addBusinessDays(shipDate, maxDays))}`;
    }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* ------------------------------------------------------------------------
     Bewertungen (Filter, Sortierung, „Mehr laden", Hilfreich-Button)
     Shopify: wird i. d. R. durch eine Review-App (Judge.me, Loox …) ersetzt.
     ------------------------------------------------------------------------ */
  class ReviewSection extends HTMLElement {
    connectedCallback() {
      this.data = Comfora.readJSON(this.querySelector('[data-reviews-json]'));
      this.list = this.querySelector('[data-review-list]');
      this.template = this.querySelector('template[data-review-template]');
      if (!this.data || !this.list || !this.template) return;

      this.moreButton = this.querySelector('[data-review-more]');
      this.sortSelect = this.querySelector('[data-review-sort]');
      this.status = this.querySelector('[data-review-status]');
      this.pageSize = parseInt(this.dataset.pageSize, 10) || 6;
      this.visibleCount = this.pageSize;
      this.filter = 'all';
      this.markedHelpful = new Set();
      this.dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

      this.renderSummary();

      this.querySelector('[data-review-filters]')?.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-filter]');
        if (!chip) return;
        this.filter = chip.dataset.filter;
        this.visibleCount = this.pageSize;
        this.querySelectorAll('[data-filter]').forEach((item) => item.setAttribute('aria-pressed', String(item === chip)));
        this.render();
      });

      this.sortSelect?.addEventListener('change', () => {
        this.visibleCount = this.pageSize;
        this.render();
      });

      this.moreButton?.addEventListener('click', () => {
        const firstNewIndex = this.visibleCount;
        this.visibleCount += this.pageSize;
        this.render();
        const firstNew = this.list.children[firstNewIndex];
        firstNew?.focus({ preventScroll: true });
      });

      this.list.addEventListener('click', (event) => {
        const button = event.target.closest('[data-helpful]');
        if (!button) return;
        const review = this.data.reviews.find((item) => item.id === button.dataset.helpful);
        if (!review || this.markedHelpful.has(review.id)) return;
        this.markedHelpful.add(review.id);
        review.helpful = (review.helpful || 0) + 1;
        this.updateHelpfulButton(button, review);
      });

      this.querySelector('[data-review-write]')?.addEventListener('click', (event) => {
        Comfora.openOverlay('review-form', event.currentTarget);
      });

      this.render();
    }

    renderSummary() {
      const { average, count, distribution } = this.data.summary;
      const decimal = (value) => value.toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

      this.querySelectorAll('[data-review-average]').forEach((el) => (el.textContent = decimal(average)));
      this.querySelectorAll('[data-review-count]').forEach((el) => (el.textContent = count.toLocaleString('en-GB')));
      this.querySelectorAll('[data-review-stars]').forEach((el) => {
        el.style.setProperty('--rating', String(average));
        el.setAttribute('aria-label', `${decimal(average)} out of 5 stars`);
      });

      const histogram = this.querySelector('[data-histogram]');
      if (!histogram) return;
      histogram.replaceChildren(
        ...[5, 4, 3, 2, 1].map((stars) => {
          const amount = distribution[stars] || 0;
          const row = document.createElement('div');
          row.className = 'histogram__row';

          const label = document.createElement('span');
          label.textContent = `${stars} ${stars === 1 ? 'star' : 'stars'}`;
          const bar = document.createElement('div');
          bar.className = 'histogram__bar';
          bar.setAttribute('role', 'img');
          bar.setAttribute('aria-label', `${amount.toLocaleString('en-GB')} reviews with ${stars} stars`);
          const fill = document.createElement('div');
          fill.className = 'histogram__fill';
          fill.style.setProperty('--fill', `${count ? (amount / count) * 100 : 0}%`);
          bar.append(fill);
          const value = document.createElement('span');
          value.textContent = amount.toLocaleString('en-GB');

          row.append(label, bar, value);
          return row;
        })
      );
    }

    get sortedAndFiltered() {
      const reviews = this.data.reviews.filter((review) => this.filter === 'all' || (review.tags || []).includes(this.filter));
      const byDate = (a, b) => b.date.localeCompare(a.date);
      const sorters = {
        newest: byDate,
        highest: (a, b) => b.rating - a.rating || byDate(a, b),
        lowest: (a, b) => a.rating - b.rating || byDate(a, b),
        helpful: (a, b) => (b.helpful || 0) - (a.helpful || 0) || byDate(a, b),
      };
      return reviews.sort(sorters[this.sortSelect?.value] || sorters.newest);
    }

    render() {
      const reviews = this.sortedAndFiltered;
      const visible = reviews.slice(0, this.visibleCount);
      this.list.replaceChildren(...visible.map((review) => this.renderReview(review)));

      if (this.moreButton) this.moreButton.hidden = visible.length >= reviews.length;
      if (this.status) {
        this.status.textContent = reviews.length
          ? `${visible.length} of ${reviews.length} reviews shown`
          : 'No reviews for this filter.';
      }
    }

    renderReview(review) {
      const node = this.template.content.firstElementChild.cloneNode(true);
      const set = (selector, text) => {
        const el = node.querySelector(selector);
        if (el) el.textContent = text;
        return el;
      };

      node.tabIndex = -1;
      set('[data-author]', review.author);
      set('[data-location]', review.location ? `from ${review.location}` : '');
      set('[data-date]', this.dateFormat.format(new Date(`${review.date}T12:00:00`)));
      set('[data-title]', review.title);
      set('[data-body]', review.body);
      set('[data-meta]', review.variant ? `Purchased: ${displayColour(review.variant).replaceAll('Größe', 'Size')}` : '');

      const stars = node.querySelector('[data-stars]');
      stars.style.setProperty('--rating', String(review.rating));
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);

      const verified = node.querySelector('[data-verified]');
      if (verified) verified.hidden = !review.verified;

      const helpfulButton = node.querySelector('[data-helpful]');
      helpfulButton.dataset.helpful = review.id;
      this.updateHelpfulButton(helpfulButton, review);

      const reply = node.querySelector('[data-reply]');
      if (reply) {
        reply.hidden = !review.reply;
        if (review.reply) reply.querySelector('[data-reply-body]').textContent = review.reply;
      }
      return node;
    }

    updateHelpfulButton(button, review) {
      const marked = this.markedHelpful.has(review.id);
      button.disabled = marked;
      button.querySelector('[data-helpful-count]').textContent = String(review.helpful || 0);
      button.setAttribute('aria-label', marked ? 'Marked as helpful' : `Helpful (${review.helpful || 0})`);
    }
  }
  customElements.define('review-section', ReviewSection);

  /* ------------------------------------------------------------------------
     Bewertung schreiben (Demo: Bestätigung für die Kundin, keine Speicherung)
     ------------------------------------------------------------------------ */
  class ReviewForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-review-form]');
      this.success = this.querySelector('[data-review-success]');
      this.error = this.querySelector('[data-review-error]');
      if (!this.form) return;
      this.ratingInputs = Array.from(this.form.querySelectorAll('input[name="rating"]'));
      this.starLabels = Array.from(this.form.querySelectorAll('.rating-input__stars label'));

      this.ratingInputs.forEach((input) =>
        input.addEventListener('change', () => {
          this.paint(this.rating());
          this.hideError();
        })
      );
      this.starLabels.forEach((label, index) => {
        label.addEventListener('mouseenter', () => this.paint(index + 1));
        label.addEventListener('mouseleave', () => this.paint(this.rating()));
      });
      this.form.addEventListener('input', () => this.hideError());
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.closest('dialog')?.addEventListener('overlay:close', () => this.reset());
    }

    rating() {
      return Number(this.ratingInputs.find((input) => input.checked)?.value || 0);
    }

    paint(value) {
      this.starLabels.forEach((label, index) => label.classList.toggle('is-on', index < value));
    }

    onSubmit(event) {
      event.preventDefault();
      const name = this.form.elements.name.value.trim();
      const body = this.form.elements.body.value.trim();
      if (!this.rating()) return this.showError('Please choose a star rating.', this.ratingInputs[0]);
      if (!name) return this.showError('Please enter your name.', this.form.elements.name);
      if (body.length < 10) return this.showError('Please write a few words about your experience.', this.form.elements.body);

      this.querySelector('[data-review-name]').textContent = `, ${name}`;
      this.form.hidden = true;
      this.success.hidden = false;
      this.success.focus();
    }

    showError(message, field) {
      this.error.textContent = message;
      this.error.hidden = false;
      field?.focus();
    }

    hideError() {
      this.error.hidden = true;
    }

    reset() {
      this.form.reset();
      this.paint(0);
      this.hideError();
      this.form.hidden = false;
      this.success.hidden = true;
    }
  }
  customElements.define('review-form', ReviewForm);

  /* ------------------------------------------------------------------------
     Produkt-Info: Varianten-Logik, Preise, Verfügbarkeit, Add-to-Cart
     ------------------------------------------------------------------------ */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.product = Comfora.readJSON(this.querySelector('[data-product-json]'));
      this.form = this.querySelector('[data-product-form]');
      if (!this.product || !this.form) return;

      [this.colorOptionName, this.sizeOptionName] = this.product.options;
      this.bundle = this.querySelector('bundle-picker');
      this.submitButton = this.form.querySelector('[data-main-atc]');
      this.submitLabel = this.form.querySelector('[data-atc-label]');
      this.variantInput = this.form.querySelector('[data-variant-id]');
      this.sizeFieldset = this.form.querySelector(`[data-option="${this.sizeOptionName}"]`);
      this.sizeError = this.form.querySelector('[data-size-error]');
      this.formError = this.form.querySelector('[data-form-error]');

      this.form.addEventListener('change', (event) => this.onChange(event));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('bundle:change', () => this.updateButton());

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-select-color]');
        if (!trigger || trigger.dataset.handle !== this.product.handle) return;
        this.selectOption(this.colorOptionName, trigger.dataset.selectColor);
        this.scrollIntoView({ behavior: Comfora.scrollBehavior(), block: 'start' });
      });

      this.applyVariantFromUrl();
      this.update();
    }

    /* --- Optionen lesen/schreiben (Radios & Selects) --- */
    getOptionInputs(name) {
      return Array.from(this.form.querySelectorAll(`[name="${CSS.escape(name)}"]`));
    }

    getOptionValue(name) {
      for (const input of this.getOptionInputs(name)) {
        if (input.type === 'radio') {
          if (input.checked) return input.value;
        } else if (input.value) {
          return input.value;
        }
      }
      return null;
    }

    setOptionValue(name, value) {
      this.getOptionInputs(name).forEach((input) => {
        if (input.type === 'radio') input.checked = input.value === value;
        else input.value = value ?? '';
      });
    }

    getSelectedOptions() {
      return this.product.options.map((name) => this.getOptionValue(name));
    }

    /** Öffentliche API (z. B. Größenberater, Farbkarten). Gibt false zurück, wenn nicht wählbar. */
    selectOption(name, value) {
      const target = this.getOptionInputs(name).find((input) =>
        input.tagName === 'SELECT' ? Array.from(input.options).some((option) => option.value === value && !option.disabled) : input.value === value
      );
      if (!target || target.disabled) return false;
      this.setOptionValue(name, value);
      if (name === this.sizeOptionName) this.hideSizeError();
      this.update();
      return true;
    }

    applyVariantFromUrl() {
      let variantId = null;
      try {
        variantId = Number(new URL(window.location.href).searchParams.get('variant'));
      } catch (error) {
        return;
      }
      const variant = this.product.variants.find((item) => item.id === variantId);
      if (!variant) return;
      this.product.options.forEach((name, index) => this.setOptionValue(name, variant[`option${index + 1}`]));
    }

    onChange(event) {
      if (!this.product.options.includes(event.target.name)) return;
      if (event.target.name === this.sizeOptionName) this.hideSizeError();
      this.hideFormError();
      this.update();
    }

    /* --- Zentrales Update nach jeder Auswahl --- */
    update() {
      const color = this.getOptionValue(this.colorOptionName);
      this.updateSizeAvailability(color);

      const options = this.getSelectedOptions();
      this.currentVariant = options.every(Boolean) ? findVariant(this.product, options) : null;
      this.displayVariant =
        this.currentVariant ||
        this.product.variants.find((variant) => variant.option1 === color && variant.available) ||
        this.product.variants.find((variant) => variant.option1 === color) ||
        this.product.variants[0];

      this.product.options.forEach((name, index) => {
        this.querySelectorAll(`[data-selected-value="${name}"]`).forEach((el) => {
          el.textContent = (index === 0 ? displayColour(options[index]) : options[index]) || el.dataset.placeholder || '';
        });
      });

      if (this.variantInput) this.variantInput.value = this.currentVariant ? String(this.currentVariant.id) : '';
      this.renderPrice(this.displayVariant);
      this.updateButton();
      this.updateUrl();

      this.dispatchEvent(
        new CustomEvent('product:variant-change', {
          bubbles: true,
          detail: { product: this.product, variant: this.currentVariant, displayVariant: this.displayVariant, options },
        })
      );
    }

    updateSizeAvailability(color) {
      let cleared = null;
      this.getOptionInputs(this.sizeOptionName).forEach((input) => {
        const check = (value) => findVariant(this.product, [color, value]);

        if (input.tagName === 'SELECT') {
          Array.from(input.options).forEach((option) => {
            if (!option.value) return;
            const variant = check(option.value);
            const available = Boolean(variant && variant.available);
            option.disabled = !available;
            option.textContent = available ? option.value : `${option.value} – ${variant ? 'sold out' : 'unavailable'}`;
            if (!available && option.selected) {
              cleared = { size: option.value, exists: Boolean(variant) };
              input.value = '';
            }
          });
          return;
        }

        const variant = check(input.value);
        const available = Boolean(variant && variant.available);
        input.disabled = !available;
        const label = this.form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (label) {
          // Größen, die es in dieser Farbe gar nicht gibt, werden ausgeblendet; ausverkaufte bleiben sichtbar.
          label.classList.toggle('size-btn--missing', !variant);
          label.title = variant ? (available ? '' : 'Sold out in this colour') : '';
        }
        if (!available && input.checked) {
          cleared = { size: input.value, exists: Boolean(variant) };
          input.checked = false;
        }
      });

      if (cleared) {
        Comfora.toast(
          cleared.exists
            ? `Size ${cleared.size} is sold out in ${displayColour(color)}. Please choose another size.`
            : `Size ${cleared.size} is not available in ${displayColour(color)}. Please choose another size.`
        );
      }
    }

    renderPrice(variant) {
      const onSale = variant.compare_at_price > variant.price;
      this.querySelectorAll('[data-price]').forEach((el) => {
        el.textContent = formatMoney(variant.price);
        el.classList.toggle('price__current--sale', onSale);
      });
      this.querySelectorAll('[data-compare-price]').forEach((el) => {
        el.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        el.hidden = !onSale;
      });
      this.querySelectorAll('[data-discount-badge]').forEach((el) => {
        el.textContent = onSale ? `–${Math.round((1 - variant.price / variant.compare_at_price) * 100)}%` : '';
        el.hidden = !onSale;
      });
      this.querySelectorAll('[data-savings-amount]').forEach((el) => {
        el.textContent = formatMoney(Math.max(0, variant.compare_at_price - variant.price));
      });
    }

    updateButton() {
      if (!this.submitButton || !this.displayVariant) return;
      if (this.currentVariant && !this.currentVariant.available) {
        this.submitButton.disabled = true;
        this.submitLabel.textContent = 'Sold out';
        return;
      }
      const quantity = this.bundle?.quantity || 1;
      const unit = this.bundle ? this.bundle.unitPrice(this.displayVariant.price) : this.displayVariant.price;
      this.submitButton.disabled = false;
      this.submitLabel.textContent = `Add to cart · ${formatMoney(unit * quantity)}`;
    }

    updateUrl() {
      if (this.dataset.updateUrl !== 'true') return;
      try {
        const url = new URL(window.location.href);
        if (this.currentVariant) url.searchParams.set('variant', String(this.currentVariant.id));
        else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        /* z. B. file:// in manchen Browsern – URL-Update ist optional */
      }
    }

    /* --- Fehlermeldungen --- */
    showSizeError() {
      if (!this.sizeFieldset) return;
      this.sizeError.hidden = false;
      this.sizeFieldset.classList.add('has-error');
      this.sizeFieldset.scrollIntoView({ behavior: Comfora.scrollBehavior(), block: 'center' });

      const values = this.sizeFieldset.querySelector('.option__values');
      values?.classList.remove('shake');
      void values?.offsetWidth;
      values?.classList.add('shake');

      const focusTarget = this.sizeFieldset.querySelector('select, input:not(:disabled)');
      focusTarget?.focus({ preventScroll: true });
    }

    hideSizeError() {
      if (this.sizeError) this.sizeError.hidden = true;
      this.sizeFieldset?.classList.remove('has-error');
    }

    showFormError(message) {
      if (!this.formError) return;
      this.formError.textContent = message;
      this.formError.hidden = false;
    }

    hideFormError() {
      if (this.formError) this.formError.hidden = true;
    }

    mediaForColor(color) {
      return this.product.media.find((item) => item.color === color) || this.product.media[0];
    }

    /* --- Add-to-Cart --- */
    async onSubmit(event) {
      event.preventDefault();
      this.hideFormError();

      if (!this.currentVariant) {
        this.showSizeError();
        return;
      }
      if (!this.currentVariant.available || this.submitButton.getAttribute('aria-busy') === 'true') return;

      const result = this.bundle
        ? this.bundle.getLines(this.currentVariant)
        : { lines: [{ variant: this.currentVariant, quantity: 1, unitPrice: this.currentVariant.price, label: '' }] };
      if (result.error) {
        this.showFormError(result.error);
        return;
      }

      // Staffelpreise werden über automatische Rabatte in Shopify berechnet.
      // _compare_at_price (versteckte Eigenschaft) dient nur der Ersparnis-Anzeige im Warenkorb.
      const items = result.lines.map(({ variant, quantity }) => ({
        id: variant.id,
        quantity,
        properties: { _compare_at_price: String(variant.compare_at_price || variant.price) },
      }));

      this.submitButton.setAttribute('aria-busy', 'true');
      try {
        await Comfora.cart.add(items);
        Comfora.openOverlay('cart-drawer', this.submitButton);
      } catch (error) {
        this.showFormError(error.message);
      } finally {
        this.submitButton.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('product-info', ProductInfo);
})();
